import {
  Technique,
  TechniqueContext,
  TechniqueType,
} from "domain/entities/techniques.entities";
import {
  HiDotsVertical,
  HiOutlineDuplicate,
  HiOutlineEye,
  HiOutlinePencil,
  HiOutlineTrash,
} from "react-icons/hi";
import { CardBgGrey400 as CardBgGrey400Square } from "ui/assets/svg/technique-card-bg-square/CardBgGrey400";
import { CardBgLightGreen400 as CardBgLightGreen400Square } from "ui/assets/svg/technique-card-bg-square/CardBgLightGreen400";
import { CardBgLightGreen500 as CardBgLightGreen500Square } from "ui/assets/svg/technique-card-bg-square/CardBgLightGreen500";
import { CardBgPaleGreen200 as CardBgPaleGreen200Square } from "ui/assets/svg/technique-card-bg-square/CardBgPaleGreen200";
import { CardBgPaleGreen400 as CardBgPaleGreen400Square } from "ui/assets/svg/technique-card-bg-square/CardBgPaleGreen400";
import { CardBgPink300 as CardBgPink300Square } from "ui/assets/svg/technique-card-bg-square/CardBgPink300";
import { CardBgPink400 as CardBgPink400Square } from "ui/assets/svg/technique-card-bg-square/CardBgPink400";
import { getDurationInHours } from "ui/utils/formatters";
import { MouseEvent } from "react";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { selectUserName } from "domain/auth/auth.selectors";
import { ModalTechnique } from "ui/pages/app/seances/create-seance/steps/add-content/modals/ModalTechnique";
import { useDisclosure } from "@chakra-ui/hooks";
import { ModalManagePersonalStep } from "ui/pages/app/seances/create-seance/steps/add-content/modals/ModalManagePersonalStep";
import { Popover, PopoverContent, PopoverTrigger } from "@chakra-ui/react";
import { translate } from "ui/i18n";
import { createPersonalStep } from "domain/seances/seances.actions";
import { getTechnique } from "domain/techniques/techniques.actions";
import { Snackbar } from "ui/pages/snackbar";
interface IProps {
  technique: Technique;
  onDelete?: () => void;
  fetchSeance: (id?: number) => Promise<void>;
  addTechnique: (
    techniqueId: number,
    context: TechniqueContext
  ) => Promise<{
    success: boolean;
  }>;
  context: TechniqueContext;
  openEditTechnique: () => void;
}
export function InlineTechniqueCard({
  technique,
  onDelete,
  fetchSeance,
  addTechnique,
  context,
  openEditTechnique,
}: IProps) {
  const CardImage = getImage(technique?.type as TechniqueType);
  const userName = useAppSelector(selectUserName);
  const modalTechnique = useDisclosure();
  const modalCreatePersonalStep = useDisclosure();
  const popoverState = useDisclosure();
  const dispatch = useAppDispatch();
  const isCustom = technique?.type === "CUSTOM";
  const isPause = technique?.type === "pause";

  const handleDeleteClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (onDelete) onDelete();
  };
  const handleCardClick = () => {
    if (isCustom) {
      modalCreatePersonalStep.onOpen();
    } else {
      modalTechnique.onOpen();
    }
  };
  const handleEditClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (isCustom) {
      handleCardClick();
    } else {
      openEditTechnique();
    }
  };

  const duplicatePersonalStep = async () => {
    const result = await dispatch(
      createPersonalStep({
        title: technique.title,
        storyline: technique.sequences[0]?.storyline || "",
        ...(technique.duration && {
          duration: Number(technique.duration),
        }),
        context,
      })
    ).unwrap();
    if (result.success && result.techniqueId) {
      const resultTechnique = await dispatch(
        getTechnique({ id: result.techniqueId })
      ).unwrap();
      if (resultTechnique.success && addTechnique && context) {
        const resultInsert = await addTechnique(
          resultTechnique.technique.id,
          context
        );
        if (resultInsert.success) {
          Snackbar.showSuccess({
            title: translate("ns1:CreateSeance.TechniqueDuplicated"),
          });
          fetchSeance();
        }
      }
    }
  };

  const handleDuplicateClick = async (e: MouseEvent) => {
    e.stopPropagation();
    if (isCustom) {
      duplicatePersonalStep();
    } else if (!isPause) {
      if (addTechnique && context) {
        const resultInsert = await addTechnique(technique.id, context);
        if (resultInsert.success) {
          Snackbar.showSuccess({
            title: translate("ns1:CreateSeance.TechniqueDuplicated"),
          });
          fetchSeance();
        }
      }
    }
  };

  return (
    <div
      className={`flex w-full flex-row items-center justify-between rounded-[16px] bg-white p-3`}
      // onClick={handleCardClick}
    >
      <div className="flex flex-row items-center">
        {!isCustom && !isPause && (
          <div style={{ width: "64px", height: "64px" }} className="me-4">
            <CardImage width="64px" height="64px" />
          </div>
        )}
        <div
          className={`flex flex-col space-y-2 ${
            isCustom || isPause ? "ms-2" : ""
          }`}
        >
          <p className="text-sm font-bold text-secondaryGrey-900">
            {technique?.title}
          </p>
          <div className="flex space-x-2">
            {(isCustom || technique.userId) && (
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondaryGrey-800 text-xs font-semibold uppercase text-secondaryGrey-500">
                {userName?.charAt(0)}
              </span>
            )}
            {technique?.duration && Number(technique?.duration) !== 0 && (
              <div className="flex w-fit items-center rounded-[36px] bg-darkGrey-200 px-3 py-1 text-xs font-semibold">
                {getDurationInHours(
                  Number(
                    technique.sequences.reduce(
                      (a, b) => a + Number(b.duration),
                      0
                    )
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Popover
          isOpen={popoverState.isOpen}
          onOpen={popoverState.onOpen}
          onClose={popoverState.onClose}
          placement="bottom-start"
        >
          <PopoverTrigger>
            <div
              className={`h-8 w-8 rounded-xl ${
                popoverState.isOpen ? " bg-darkGrey-200" : ""
              }`}
            >
              <button className="flex h-8 w-8 items-center justify-center">
                <HiDotsVertical size={20} color="#485585" />
              </button>
            </div>
          </PopoverTrigger>
          <PopoverContent
            style={{
              isolation: "isolate",
              zIndex: 100,
              width: "fit-content",
              border: "none",
              backgroundColor: "transparent",
              boxShadow: "none",
            }}
          >
            <div className="flex flex-col rounded-[20px] bg-white px-2 py-3 text-sm font-semibold text-darkGrey-700 shadow-lg">
              {!isCustom && (
                <button
                  className="flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100"
                  onClick={handleCardClick}
                >
                  <HiOutlineEye
                    className="me-2 text-secondaryGrey-900"
                    size={20}
                  />
                  {translate("ns1:Button.Consult")}
                </button>
              )}
              {!isPause && (
                <button
                  className="flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100"
                  onClick={handleEditClick}
                >
                  <HiOutlinePencil
                    className="me-2 text-secondaryGrey-900"
                    size={20}
                  />
                  {translate("ns1:Button.Edit")}
                </button>
              )}
              {!isPause && !technique.userId && (
                <button
                  className="flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100"
                  onClick={handleDuplicateClick}
                >
                  <HiOutlineDuplicate
                    className="me-2 text-secondaryGrey-900"
                    size={20}
                  />
                  {translate("ns1:Button.Duplicate")}
                </button>
              )}
              <button
                className="flex rounded-[10px] px-3 py-2 text-error hover:bg-darkGrey-100"
                onClick={handleDeleteClick}
              >
                <HiOutlineTrash className="me-2 text-error" size={20} />
                {translate("ns1:Button.Delete")}
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <ModalTechnique
        partialTechnique={technique}
        isOpen={modalTechnique.isOpen}
        closeViewTechniqueModal={modalTechnique.onClose}
      />
      <ModalManagePersonalStep
        isOpen={modalCreatePersonalStep.isOpen}
        onClose={modalCreatePersonalStep.onClose}
        personalStep={technique}
        fetchSeance={fetchSeance}
        context={technique?.context as TechniqueContext}
      />
    </div>
  );
}
function getImage(activityType: TechniqueType) {
  switch (activityType) {
    case "ice_breaker":
      return CardBgLightGreen500Square;
    case "equipes":
      return CardBgPaleGreen400Square;
    case "brainstorming":
      return CardBgPink300Square;
    case "projects":
      return CardBgPaleGreen200Square;
    case "evaluation":
      return CardBgPink400Square;
    case "pause":
      return CardBgLightGreen400Square;
    case "vote":
      return CardBgGrey400Square;
    default:
      return CardBgPink400Square;
  }
}
